import { Appointment, TaskListItem, TechnicianAppointmentsItem } from '@myapp/contract'
import { resolveBreakForDay } from '../../../entities/time-entry/model/useBreakBlock'
import { isWorkingDay } from '../../../shared/lib/timeline/weekHelpers'
import { getHourFromIso } from '../../worker-calendar/model/calendarConstants'
import { getTimeEntryCategoryLabel } from '../../../entities/time-entry'

const BREAK_DURATION_MIN = 30

export interface ConflictItem {
  label: string
  timeLabel: string
}

interface DraftRange {
  plannedStart: string // work start ISO
  travelDuration: number // minutes
  workDuration: number // minutes
  departureDuration: number // minutes
}

function overlaps(startA: number, endA: number, startB: number, endB: number): boolean {
  return startA < endB && startB < endA
}

function toMs(iso: string): number {
  return new Date(iso).getTime()
}

function formatTime(ms: number): string {
  const d = new Date(ms)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export function checkCollisions(
  draft: DraftRange,
  technicianData: TechnicianAppointmentsItem | null,
  existingTasks: TaskListItem[],
): ConflictItem[] {
  const workStartMs = toMs(draft.plannedStart)
  const draftStart = workStartMs - draft.travelDuration * 60_000
  const draftEnd = workStartMs + (draft.workDuration + draft.departureDuration) * 60_000

  const conflicts: ConflictItem[] = []
  const dayIso = new Date(draft.plannedStart).toISOString().slice(0, 10)

  // --- Past-time check ---
  if (draftStart < Date.now()) {
    conflicts.push({ label: 'Zeitpunkt liegt in der Vergangenheit', timeLabel: '' })
    return conflicts
  }

  // --- Working day & shift hours ---
  if (technicianData) {
    if (!isWorkingDay(technicianData.workingDays, dayIso)) {
      conflicts.push({ label: 'Kein Arbeitstag', timeLabel: '' })
      return conflicts
    }

    // Shift hours: stored as "local time as UTC" → getUTCHours() = intended local hour
    const shiftStartHour = technicianData.shiftStart ? getHourFromIso(technicianData.shiftStart) : null
    const shiftEndHour = technicianData.shiftEnd ? getHourFromIso(technicianData.shiftEnd) : null

    // Draft times as hour-of-day (local getHours matches the "stored-as-UTC" convention)
    const draftStartHour = new Date(draftStart).getHours() + new Date(draftStart).getMinutes() / 60
    const draftEndHour = new Date(draftEnd).getHours() + new Date(draftEnd).getMinutes() / 60

    if (shiftStartHour != null && shiftEndHour != null && (draftStartHour < shiftStartHour || draftEndHour > shiftEndHour)) {
      conflicts.push({ label: 'Außerhalb der Arbeitszeit', timeLabel: '' })
    }
  }

  // --- Appointments ---
  if (technicianData) {
    const dayAppointments = technicianData.appointments.filter(
      (a) => !a.needsRescheduling && new Date(a.plannedStart).toISOString().slice(0, 10) === dayIso,
    )

    for (const appt of dayAppointments) {
      const isCompleted = appt.status === 'COMPLETED' || appt.status === 'COMPLETED_WITH_FOLLOW_UP'
      const hasActualData = isCompleted &&
        appt.actualStart != null &&
        appt.actualTravelDuration != null &&
        appt.actualWorkDuration != null

      const travelDur = hasActualData ? appt.actualTravelDuration! : appt.plannedTravelDuration
      const workDur = hasActualData ? appt.actualWorkDuration! : appt.plannedWorkDuration
      const departureDur = (hasActualData && appt.actualDepartureDuration != null)
        ? appt.actualDepartureDuration
        : appt.plannedDepartureDuration

      // Mirror WorkerAppointmentBlock: actualStart = початок роботи, рахуємо вперед
      const workStartMs = hasActualData ? toMs(appt.actualStart!) : toMs(appt.plannedStart)
      const apptStart = workStartMs - travelDur * 60_000
      const apptEnd = workStartMs + (workDur + departureDur) * 60_000

      if (overlaps(draftStart, draftEnd, apptStart, apptEnd)) {
        const address = appt.workOrder?.external.gebaeude ?? 'Termin'
        conflicts.push({
          label: address,
          timeLabel: formatTime(workStartMs),
        })
      }
    }

    // --- TM entries ---
    const dayStart = new Date(`${dayIso}T00:00:00`).getTime()
    const dayEnd = new Date(`${dayIso}T23:59:59.999`).getTime()

    const dayTMEntries = technicianData.timeManagementEntries.filter(
      (e) => toMs(e.startTime) <= dayEnd && toMs(e.endTime) >= dayStart,
    )

    for (const entry of dayTMEntries) {
      const entryStart = Math.max(toMs(entry.startTime), dayStart)
      const entryEnd = Math.min(toMs(entry.endTime), dayEnd)

      if (overlaps(draftStart, draftEnd, entryStart, entryEnd)) {
        conflicts.push({
          label: getTimeEntryCategoryLabel(entry.category),
          timeLabel: formatTime(entryStart),
        })
      }
    }

    // --- Break ---
    if (isWorkingDay(technicianData.workingDays, dayIso)) {
      const breakBlock = resolveBreakForDay(technicianData, dayIso)
      const breakStart = toMs(breakBlock.startTimeIso)
      const breakEnd = breakStart + BREAK_DURATION_MIN * 60_000

      if (overlaps(draftStart, draftEnd, breakStart, breakEnd)) {
        conflicts.push({
          label: 'Pause',
          timeLabel: formatTime(breakStart),
        })
      }
    }
  }

  // --- Existing tasks (excluding current) ---
  for (const task of existingTasks) {
    if (!task.plannedStart) continue
    if (new Date(task.plannedStart).toISOString().slice(0, 10) !== dayIso) continue

    const taskWorkMs = toMs(task.plannedStart)
    const taskStart = taskWorkMs - task.plannedTravelDuration * 60_000
    const taskEnd = taskWorkMs + (task.plannedWorkDuration + task.plannedDepartureDuration) * 60_000

    if (overlaps(draftStart, draftEnd, taskStart, taskEnd)) {
      conflicts.push({
        label: task.title,
        timeLabel: formatTime(taskWorkMs),
      })
    }
  }

  return conflicts
}
